import { assertExists } from "helpers/assertExists";
import { ExperimentConfig, ExperimentConfigVal } from "./types";

function typecheckArray(
  firstArr: Array<string | number | boolean>,
  secondArr: Array<string | number | boolean>,
) {
  if (firstArr.length !== secondArr.length) {
    return false;
  }

  return firstArr.every((val, idx) => typeof val === typeof secondArr[idx]);
}

// Ensure that all keys in first config are present in second config, and
// have a matching type. We explicitly allow second config to have 'extra'
// keys to account for scenarios where configs are briefly out of sync (e.g.
// because we're pushing changes to one before we push changes to the other).
export function typecheckConfig(
  firstConfig: ExperimentConfig,
  secondConfig: ExperimentConfig | null,
): boolean {
  assertExists(firstConfig);

  if (secondConfig === null) {
    return false;
  }

  const firstKeys = Object.keys(firstConfig);

  return firstKeys.every(
    (key) =>
      key in secondConfig &&
      typecheckConfigValue(firstConfig[key], secondConfig[key]),
  );
}

// Only exported for testing purposes.
export function typecheckConfigValue(
  firstVal: ExperimentConfigVal,
  secondVal: ExperimentConfigVal | null,
) {
  assertExists(firstVal);

  if (secondVal === null) {
    return false;
  }

  const firstType = typeof firstVal;

  if (firstType !== typeof secondVal) {
    return false;
  }

  switch (firstType) {
    case "number":
    case "string":
    case "boolean":
      // already verified types match above
      return true;
    case "object":
      if (Array.isArray(firstVal)) {
        return Array.isArray(secondVal) && typecheckArray(firstVal, secondVal);
      }
      return typecheckConfig(
        firstVal as ExperimentConfig,
        secondVal as ExperimentConfig,
      );

    default:
      throw new Error(`unexpected type: ${firstType}`);
  }
}
